import React, { useEffect, useState } from "react";
import { Form, Input, message, Upload, Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, getStorageUrl } from "../../../shared/api/AllApi";
import { invalidateCatalog } from "../../../hooks/useCatalogData";
import usePermissions from "../../../hooks/usePermissions";
import { parseLocalizedName } from "../../../utils/localization";
import { FormPage, FormCard, inputStyle, T } from "../../../shared/ui/PageShell";

const UpdateProducts = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManageImages = hasPermission("products.images");
  const [form] = Form.useForm();
  const [product, setProduct] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);

  useEffect(() => {
    apiClient.get(`/products/${id}`)
      .then((res) => {
        const p = res.data;
        setProduct(p);
        setImageUrl(p.image_url || null);
        form.setFieldsValue({
          name: parseLocalizedName(p.name, i18n.language, typeof p.name === "string" ? p.name : ""),
        });
      })
      .catch(() => message.error(t("productLoadFailed", "Failed to load product details")))
      .finally(() => setLoadingProduct(false));
  }, [id, form, t, i18n.language]);

  const handleImageUpload = async (file) => {
    const extension = file.name?.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type)
      || ![".jpg", ".jpeg", ".png", ".webp"].includes(extension)
      || file.size > 4 * 1024 * 1024
    ) {
      message.error(t("productImageInvalid", "Use a JPG, PNG, or WebP image up to 4 MB."));
      return;
    }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/products/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.status === 200) {
        setImageUrl(res.data.path);
        setImageChanged(true);
      }
      else message.error(t("errorMessage", "Failed to submit form. Please try again."));
    } catch { message.error(t("errorMessage", "Failed to submit form. Please try again.")); }
    finally { setImageUploading(false); }
  };

  const onSave = async () => {
    if (!product) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const nameVal = (values.name || "").trim();
      // Only name + image are edited here; price / stock / sku are preserved.
      const payload = {
        name: JSON.stringify({ en: nameVal, ar: nameVal, ku: nameVal }),
      };
      if (canManageImages && imageChanged && imageUrl) payload.image_url = imageUrl;
      const res = await apiClient.put(`/products/${id}`, payload);
      if (res.status === 200) {
        invalidateCatalog("products");
        message.success(t("productUpdated", "Product updated successfully"));
        navigate("/products");
      } else {
        message.error(t("productUpdateFailed", "Failed to update product"));
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error(t("productUpdateFailed", "Failed to update product"));
    } finally { setSubmitting(false); }
  };

  if (loadingProduct) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300, background: T.bg }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return <div style={{ padding: 24 }}>{t("productNotFound", "Product not found")}</div>;
  }

  const existingImage = product?.image_url ? getStorageUrl(product.image_url) : null;

  return (
    <FormPage
      breadcrumb={[t("sidebarSectionCatalog", "Catalog"), t("products", "Products"), t("edit", "Edit")]}
      title={t("updateProduct", "Update Product")}
      subtitle={t("updateProductSubtitle", "Update product details")}
      onBack={() => navigate("/products")}
      onSave={onSave}
      saving={submitting}
      sidePanel={canManageImages ? (
        <FormCard title={t("image", "Image")}>
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => { handleImageUpload(file); return false; }}
              disabled={imageUploading}
            >
              {imageUrl || existingImage ? (
                <img
                  src={imageUrl ? getStorageUrl(imageUrl) : existingImage}
                  alt={t("productImageAlt", "Product")}
                  style={{ width: 140, height: 140, objectFit: "cover", borderRadius: 12, border: `2px dashed ${T.border}`, cursor: "pointer" }}
                />
              ) : (
                <div style={{ width: 140, height: 140, borderRadius: 12, border: `2px dashed ${T.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 8, color: T.muted, margin: "0 auto" }}>
                  <UploadOutlined style={{ fontSize: 28 }} />
                  <span style={{ fontSize: 12 }}>{t("uploadImage", "Upload image")}</span>
                </div>
              )}
            </Upload>
          </div>
        </FormCard>
      ) : null}
    >
      <Form form={form} layout="vertical" requiredMark scrollToFirstError>
        <FormCard title={t("productInfo", "Product Information")}>
          <Form.Item name="name" label={t("name", "Name")} rules={[{ required: true, message: t("enterProductName", "Enter product name") }]}>
            <Input placeholder={t("enterProductName", "Enter product name")} style={inputStyle} />
          </Form.Item>
        </FormCard>
      </Form>
    </FormPage>
  );
};

export default UpdateProducts;
